import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums';
import { resolveEffectiveMimeType } from '../FileUpload/FileUpload.service';
import {
  IMAGE_HEADER_BYTES,
  MAX_HEIC_INPUT_BYTES,
  MAX_IMAGE_INPUT_BYTES,
  MAX_VIDEO_INPUT_BYTES,
  PNG_MAX_ACCEPTED_BIT_DEPTH,
} from './MediaCompression.constants';
import {
  getCompressibleKind,
  isTranscodeOnlyImage,
  isWapiAcceptedType,
  readPngBitDepth,
  readWebpKind,
} from './MediaCompression.functions';
import { HeicTranscodeService } from './HeicTranscode.service';
import { ImageCompressionService } from './ImageCompression.service';
import { VideoCompressionService } from './VideoCompression.service';
import {
  eCompressionFailReason,
  tCompressMediaResult,
  tCompressToLimitRequest,
  tNormalizeForChannelRequest,
  tNormalizeImageResult,
  tNormalizeImageSuccess,
} from './MediaCompression.types';

// Original file → compressed file, so preview-upload + send (or send retries)
// only pay the encode cost once per attached file.
const compressionCache = new WeakMap<File, File>();

// Original file → in-flight compression, so an eager (on-add) compression and a
// later send of the same file coalesce onto one encode instead of running two
// concurrently (which would double memory and contend for the HW encoder). The
// first caller's onProgress drives the UI; later callers just await the result.
const inFlightCompressions = new WeakMap<File, Promise<tCompressMediaResult>>();

// Original HEIC file → transcoded JPEG result, so the same file decodes only
// once. The drag/drop send path re-runs sendFileMessage on the original HEIC at
// both preview and send (and again on retry), and HEIC decode is a multi-second
// wasm operation — memoize it like compressionCache does for size compression.
// No in-flight guard is needed: image transcode has no concurrent caller (the
// send loop is sequential and the eager pre-warm queue is video-only).
const transcodeCache = new WeakMap<File, tNormalizeImageSuccess>();

function getInputBytesCap(kind: 'image' | 'video'): number {
  return kind === 'image' ? MAX_IMAGE_INPUT_BYTES : MAX_VIDEO_INPUT_BYTES;
}

async function runCompression(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const { file, maxBytes, onProgress } = request;

  const kind = getCompressibleKind(file);
  if (kind === null) {
    return { ok: false, reason: eCompressionFailReason.UNSUPPORTED_TYPE };
  }
  if (file.size > getInputBytesCap(kind)) {
    return { ok: false, reason: eCompressionFailReason.INPUT_TOO_LARGE };
  }

  return kind === 'image'
    ? ImageCompressionService.compressImageToLimit({ file, maxBytes })
    : VideoCompressionService.compressVideoToLimit({ file, maxBytes, onProgress });
}

/**
 * Sync pre-check for validation layers: could compressFileToLimit plausibly
 * bring this file under a limit? The definitive answer (encoder support,
 * achievable bitrate) is only known once compression actually runs.
 */
function canCompressFile(file: File): boolean {
  // HEIC/HEIF are brought under the channel limit by transcoding to JPEG (then
  // the size ladder), so the send-path pre-flight gates must treat them as
  // compressible — otherwise an oversized iPhone photo is rejected before the
  // transcode ever runs. getCompressibleKind only knows the natively-encodable
  // formats, so answer for the transcode path here. Must use the same cap
  // normalizeImageForCompatibility enforces, or the gate lets through a file the
  // transcode will then reject.
  if (isTranscodeOnlyImage(file)) {
    return file.size <= MAX_HEIC_INPUT_BYTES;
  }

  const kind = getCompressibleKind(file);
  if (kind === null) return false;
  if (file.size > getInputBytesCap(kind)) return false;

  const hasVideoEncoder =
    typeof (globalThis as { VideoEncoder?: unknown }).VideoEncoder !==
    'undefined';
  if (kind === 'video' && !hasVideoEncoder) return false;

  return true;
}

function passThrough(file: File): tCompressMediaResult {
  return {
    ok: true,
    file,
    wasCompressed: false,
    originalBytes: file.size,
    finalBytes: file.size,
  };
}

/**
 * Serves a cached result, or joins the in-flight run for the same File, or
 * starts run and caches what it produces. A result that hands back the
 * original (a remux that failed open) is cached too, so the eager run and the
 * send do not pay for the same failed remux twice. That is safe for
 * compressFileToLimit: an oversize original cached as itself fails the
 * size check below and is compressed.
 */
async function runCoalesced(
  request: tCompressToLimitRequest,
  run: (request: tCompressToLimitRequest) => Promise<tCompressMediaResult>,
): Promise<tCompressMediaResult> {
  const { file, maxBytes } = request;

  const cachedFile = compressionCache.get(file);
  if (cachedFile && cachedFile.size <= maxBytes) {
    return {
      ok: true,
      file: cachedFile,
      wasCompressed: cachedFile !== file,
      originalBytes: file.size,
      finalBytes: cachedFile.size,
    };
  }

  const existing = inFlightCompressions.get(file);
  if (existing) {
    return existing;
  }

  const compressionPromise = run(request);
  inFlightCompressions.set(file, compressionPromise);

  try {
    const result = await compressionPromise;
    if (result.ok) {
      compressionCache.set(file, result.file);
    }
    return result;
  } finally {
    inFlightCompressions.delete(file);
  }
}

async function compressFileToLimit(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  if (request.file.size <= request.maxBytes) {
    return passThrough(request.file);
  }
  return runCoalesced(request, runCompression);
}

// FileReader instead of Blob.arrayBuffer(): same bytes in browsers, and
// jsdom's Blob has no arrayBuffer().
function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

async function readImageHeader(file: File): Promise<Uint8Array | null> {
  try {
    return await readBlobBytes(file.slice(0, IMAGE_HEADER_BYTES));
  } catch {
    // Unreadable header: judge the file by its type alone, as before.
    return null;
  }
}

function isHighBitDepthPng(header: Uint8Array | null): boolean {
  const bitDepth = header ? readPngBitDepth(header) : null;
  return bitDepth !== null && bitDepth > PNG_MAX_ACCEPTED_BIT_DEPTH;
}

/**
 * Images Meta rejects (GIF, HEIC, BMP, WebP, PNGs above 8 bits per channel)
 * are re-encoded as JPEG at any size; accepted ones keep the size-only
 * behavior. HEIC fails to decode in most browsers and comes back ok: false.
 *
 * INT-1683: a WebP is told by its bytes, so a still one is converted even
 * when the browser labels it image/jpeg. An animated one goes out as it is:
 * converting it would send only its first frame, and
 * whatsapp-business-service refuses it with a notice that says why.
 */
async function normalizeWapiImage(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const { file, maxBytes } = request;
  const type = resolveEffectiveMimeType(file);
  const header = await readImageHeader(file);
  const webpKind = header ? readWebpKind(header) : null;
  if (webpKind === 'animated') return passThrough(file);

  const needsConversion =
    webpKind === 'static' ||
    !isWapiAcceptedType(type) ||
    (type === 'image/png' && isHighBitDepthPng(header));

  if (!needsConversion) {
    return file.size <= maxBytes ? passThrough(file) : runCompression(request);
  }
  if (file.size > MAX_IMAGE_INPUT_BYTES) {
    return { ok: false, reason: eCompressionFailReason.INPUT_TOO_LARGE };
  }
  return ImageCompressionService.compressImageToLimit({ file, maxBytes });
}

/**
 * Every WAPI video under the limit is rewritten into a fresh MP4 (copying
 * streams that already fit); oversize ones, and remuxes that come out
 * oversize, take the bitrate-targeted compression. A failure on an
 * allow-listed type (MP4, 3GPP) sends the original, as before; a failure on
 * anything else, or a file with no video stream, blocks the send.
 */
async function normalizeWapiVideo(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const { file, maxBytes } = request;
  if (file.size > MAX_VIDEO_INPUT_BYTES) {
    return { ok: false, reason: eCompressionFailReason.INPUT_TOO_LARGE };
  }
  if (file.size > maxBytes) {
    return VideoCompressionService.compressVideoToLimit(request);
  }

  let result = await VideoCompressionService.remuxVideoToMp4(request);
  if (!result.ok && result.reason === eCompressionFailReason.OUTPUT_TOO_LARGE) {
    result = await VideoCompressionService.compressVideoToLimit(request);
  }

  const isAccepted = isWapiAcceptedType(resolveEffectiveMimeType(file));
  if (
    result.ok ||
    result.reason === eCompressionFailReason.NO_VIDEO_TRACK ||
    !isAccepted
  ) {
    return result;
  }
  return passThrough(file);
}

async function runWapiNormalization(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const type = resolveEffectiveMimeType(request.file);
  if (type.startsWith('image/')) return normalizeWapiImage(request);
  if (type.startsWith('video/')) return normalizeWapiVideo(request);
  return request.file.size <= request.maxBytes
    ? passThrough(request.file)
    : runCompression(request);
}

/**
 * Leaves the file in a format the channel accepts. On the WhatsApp Cloud API
 * that means converting by format, not only by size: a file under the limit
 * in a format Meta rejects would otherwise go out raw and fail with
 * 3026 / wba-10015. Every other channel keeps the size-only behavior.
 */
async function normalizeFileForChannel(
  request: tNormalizeForChannelRequest,
): Promise<tCompressMediaResult> {
  const { channel, ...compressRequest } = request;
  if (channel !== eChannelType.WHATSAPP_API) {
    return compressFileToLimit(compressRequest);
  }
  return runCoalesced(compressRequest, runWapiNormalization);
}

/**
 * Sync pre-check for the send path: will normalizeImageForCompatibility actually
 * decode this file (a multi-second wasm operation for large photos)? Lets
 * callers show a "processing" indicator before starting, without awaiting the
 * transcode.
 *
 * A cached file answers false: the drop path calls sendFileMessage twice for the
 * same File (once to build the preview, once to send), and the second call is a
 * pure cache hit. Answering on format alone would make that instant call flash a
 * "this may take a few seconds" toast. Distinct from isTranscodeOnlyImage, which
 * answers the format question ("is this HEIC?") rather than the work question.
 */
function willTranscodeImage(file: File): boolean {
  return isTranscodeOnlyImage(file) && !transcodeCache.has(file);
}

/**
 * Ensures an image is in a browser-renderable format before send. HEIC/HEIF
 * (iPhone photos) are transcoded to JPEG — Chrome/Firefox/Edge can't decode
 * them, so left as-is they show a broken/"expired" bubble and may not deliver.
 * All other images pass through untouched. This is format-driven and runs
 * regardless of size; size compression (compressFileToLimit) runs separately,
 * after this, on the resulting JPEG.
 */
async function normalizeImageForCompatibility(
  file: File,
): Promise<tNormalizeImageResult> {
  if (!isTranscodeOnlyImage(file)) {
    return { ok: true, file, wasTranscoded: false };
  }

  // Tighter than runCompression's native-decode cap: libheif's wasm heap only
  // grows and its worker is never torn down, so an oversized decode permanently
  // raises this tab's memory ceiling. See MAX_HEIC_INPUT_BYTES.
  if (file.size > MAX_HEIC_INPUT_BYTES) {
    return { ok: false, reason: eCompressionFailReason.INPUT_TOO_LARGE };
  }

  const cached = transcodeCache.get(file);
  if (cached) {
    return cached;
  }

  const result = await HeicTranscodeService.transcodeHeicToJpeg(file);
  if (result.ok) {
    transcodeCache.set(file, result);
  }
  return result;
}

export const MediaCompressionService = Object.freeze({
  canCompressFile,
  compressFileToLimit,
  normalizeFileForChannel,
  willTranscodeImage,
  normalizeImageForCompatibility,
});
